import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../contexts/AppContext';

import FloatingFooter from '../components/utils/FloatingFooter';
import ConfirmModal from '../components/utils/ConfirmModal';
import InformModal from '../components/utils/InformModal';

import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { message } from 'antd';

const HomeQuickLink = () => {
  const { token, selectedRole, userInfo } = useContext(AppContext);

  const [originLanding, setOriginLanding] = useState({});
  const [selected, setSelected] = useState([]);
  const [pool, setPool] = useState({});
  const [asc, setAsc] = useState(true);
  const [qPool, setQPool] = useState('');
  const [qSel, setQSel] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [informOpen, setInformOpen] = useState(false);
  const [informMsg, setInformMsg] = useState('');
  const [informType, setInformType] = useState('inform');

  useEffect(() => {
    if (!userInfo || !userInfo.roleInfo || !selectedRole?.id) return;

    const list = userInfo.roleInfo[selectedRole.type] || [];
    const matched = list.find((r) => r.id === selectedRole.id);

    const landingObj = matched?.landing || {};
    setOriginLanding(landingObj);
    setSelected(landingObj.quickLinkIds || []);

    const fetchLeafNames = async () => {
      try {
        const resp = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/content/all-nodes/list`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (resp.ok) {
          const data = await resp.json();
          setPool(data);
        }
      } catch (e) {
        console.error('fetch leaf names error', e);
      }
    };

    fetchLeafNames();
  }, [userInfo, selectedRole, token]);

  const add = (id) => {
    if (userInfo?.permissionLevel === 0 && selected.length >= 2) {
      message.error(
        'Your package level only allows up to 2 Quick Links. Please contact support to upgrade your package.'
      );
      return;
    }
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
    }
  };

  const remove = (id) => setSelected(selected.filter((x) => x !== id));
  const up = (i) => i > 0 && swap(i, i - 1);
  const down = (i) => i < selected.length - 1 && swap(i, i + 1);
  const swap = (i, j) => {
    const arr = [...selected];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSelected(arr);
  };

  const save = async () => {
    const body = {
      landing: { ...originLanding, quickLinkIds: selected },
    };

    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/property/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (resp.ok) return { ok: true };
      const data = await resp.json().catch(() => ({}));
      return { ok: false, message: data.error || resp.statusText };
    } catch (err) {
      return { ok: false, message: 'Network error' };
    }
  };

  const onConfirmSave = async () => {
    const { ok, message } = await save();
    setConfirmOpen(false);
    if (ok) window.location.reload();
    else {
      setInformMsg(message);
      setInformType('error');
      setInformOpen(true);
    }
  };

  const poolEntries = Object.entries(pool)
    .filter(([id, name]) => !selected.includes(id) && name.toLowerCase().includes(qPool))
    .sort((a, b) => (asc ? a[1].localeCompare(b[1]) : b[1].localeCompare(a[1])));

  const selectedFiltered = selected.filter((id) => (pool[id] || '').toLowerCase().includes(qSel));

  const hasChanges = useMemo(
    () => JSON.stringify(selected) !== JSON.stringify(originLanding.quickLinkIds || []),
    [selected, originLanding]
  );

  return (
    <div className="mx-auto mb-20 w-[90%] max-w-7xl">
      <div className="mt-4 overflow-hidden rounded-[30px] border border-[#e2e7f6] bg-white shadow-[0_18px_42px_rgba(15,23,42,0.05)]">
        <div className="grid grid-cols-1 divide-y divide-[#e8edf8] md:grid-cols-2 md:divide-x md:divide-y-0">
          <section className="px-7 py-5 transition-colors duration-200 hover:bg-[#fcfdff]">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div className="cms-section-title text-[#1f2a44]">
                Selected Links
              </div>
              <div className="rounded-full border border-[#dde5fb] bg-white px-3 py-1.5 text-[12px] font-medium text-[#5e6d8a]">
                {selected.length} chosen
              </div>
            </div>

            <input
              className="w-full rounded-[14px] border border-[#d6deef] bg-white px-4 py-3 text-[14px] text-[#24324d] outline-none transition-all duration-200 placeholder:text-[#99a4bc] focus:border-[#aebcf1] focus:shadow-[0_0_0_4px_rgba(74,92,255,0.08)]"
              placeholder="Search selected links"
              value={qSel}
              onChange={(e) => setQSel(e.target.value.toLowerCase())}
            />

            <ul className="mt-4 divide-y divide-[#e8edf8]">
              {selectedFiltered.map((id, idx) => (
                <li
                  key={id}
                  className="flex items-center gap-3 px-2 py-3 transition-colors duration-200 hover:bg-[#fbfcff]"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#4a5cff] text-sm font-semibold text-white shadow-[0_10px_20px_rgba(74,92,255,0.18)]">
                    {idx + 1}
                  </span>
                  <a
                    href={`${window.location.origin}/detail/${id}`}
                    className="min-w-0 flex-1 truncate text-[15px] font-medium text-[#24324d] transition-colors duration-200 hover:text-[#4a5cff]"
                  >
                    {pool[id] || id}
                  </a>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={idx === 0}
                      onClick={() => up(idx)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#dde5f4] bg-white text-[#73819b] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#ccd7f2] hover:bg-[#f8faff] hover:text-[#24324d] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <FaArrowUp />
                    </button>
                    <button
                      disabled={idx === selected.length - 1}
                      onClick={() => down(idx)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#dde5f4] bg-white text-[#73819b] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#ccd7f2] hover:bg-[#f8faff] hover:text-[#24324d] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <FaArrowDown />
                    </button>
                    <button
                      onClick={() => remove(id)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#ffd9e0] bg-[#fff5f7] text-[#ff4d6d] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#ffc5cf] hover:bg-[#fff0f3] hover:shadow-[0_10px_20px_rgba(255,77,109,0.12)]"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="px-7 py-5 transition-colors duration-200 hover:bg-[#fcfdff]">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div className="cms-section-title text-[#1f2a44]">
                Available Links
              </div>
              <button
                onClick={() => setAsc(!asc)}
                className="inline-flex items-center rounded-full border border-[#d9e3ff] bg-[#f4f7ff] px-4 py-2 text-[13px] font-medium text-[#4a5cff] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#c8d3fb] hover:bg-[#eef2ff] hover:shadow-[0_10px_20px_rgba(74,92,255,0.12)]"
              >
                {asc ? 'Descend' : 'Ascend'}
              </button>
            </div>

            <input
              className="w-full rounded-[14px] border border-[#d6deef] bg-white px-4 py-3 text-[14px] text-[#24324d] outline-none transition-all duration-200 placeholder:text-[#99a4bc] focus:border-[#aebcf1] focus:shadow-[0_0_0_4px_rgba(74,92,255,0.08)]"
              placeholder="Search available links"
              value={qPool}
              onChange={(e) => setQPool(e.target.value.toLowerCase())}
            />

            <div className="mt-4">
              {poolEntries.length ? (
                <ul className="divide-y divide-[#e8edf8]">
                  {poolEntries.map(([id, name]) => (
                    <li
                      key={id}
                      className="flex items-center gap-3 px-2 py-3 transition-colors duration-200 hover:bg-[#fbfcff]"
                    >
                      <a
                        href={`${window.location.origin}/detail/${id}`}
                        className="min-w-0 flex-1 truncate text-[15px] font-medium text-[#24324d] transition-colors duration-200 hover:text-[#4a5cff]"
                      >
                        {name}
                      </a>
                      <button
                        onClick={() => add(id)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#d6f3df] bg-[#effcf4] text-[#1faa63] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#c0ebcf] hover:bg-[#e7f9ee] hover:shadow-[0_10px_20px_rgba(31,170,99,0.12)]"
                      >
                        <FaPlus />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="rounded-[18px] border border-[#e8edf8] bg-[#fbfcff] px-4 py-4 text-[14px] text-[#7a88a6]">
                  No results found.
                </p>
              )}
            </div>
          </section>
        </div>
      </div>

      <FloatingFooter hasChanges={hasChanges} onSave={() => setConfirmOpen(true)} />

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
        header="Confirm Save"
        content="Are you sure you want to save these quick links?"
      />

      <InformModal
        isOpen={informOpen}
        onClose={() => setInformOpen(false)}
        content={informMsg}
        type={informType}
      />
    </div>
  );
};

export default HomeQuickLink;
